import React, { useEffect, useState } from 'react'
import Modal from '../../componants/modal/Modal'
import { SampleUser } from '../../assests/ExportImageIcon'
import AvatarModal from '../onboarding/AvatarModal'
import DatePicker from '../../componants/datePicker/DatePicker'
import SearchableSelect from '../../componants/searchableSelect/SearchableSelect'
import service from '../../services/service'
import endpoints from '../../services/endpoints'
import { showToast } from '../../componants/toast/toastService'
import { resolveAvatarId, resolveProfileImage, withProfileImage } from '../../utils/profileImage'

const DEFAULT_FORM = {
  first_name: '',
  last_name: '',
  gender: 'M',
  dob: '',
  country_id: '',
  city_id: '',
  phone: '',
  phone_code: '+91',
  language_id: '',
  currency: '',
  notifications_enabled: true,
  avatar: '',
  email: '',
}

const mapAccountToForm = (account = {}) => ({
  first_name: account.first_name || '',
  last_name: account.last_name || '',
  gender: account.gender || 'M',
  dob: account.dob || '',
  country_id: account.country?.id ?? account.country_id ?? '',
  city_id: account.city?.id ?? account.city_id ?? '',
  phone: account.phone || '',
  phone_code: account.phone_code || '+91',
  language_id: account.language?.id ?? account.language_id ?? '',
  currency: account.currency || account.currency_code || '',
  notifications_enabled: account.notifications_enabled ?? true,
  avatar: resolveAvatarId(account),
  email: account.email || '',
})

const getCurrencyLabel = (item) => {
  const name = item?.name || item?.title || ''
  const code = item?.code || ''
  const symbol = item?.symbol || item?.currency_symbol || ''
  if (name && code) return `${name} (${code}${symbol ? ` • ${symbol}` : ''})`
  return name || code || '—'
}

const EditProfile = ({ open, onClose, initialData = null, profilePicUrl = '', onSaved }) => {
  const [saving, setSaving] = useState(false)
  const [countries, setCountries] = useState([])
  const [cities, setCities] = useState([])
  const [languages, setLanguages] = useState([])
  const [currencies, setCurrencies] = useState([])
  const [profilePicFile, setProfilePicFile] = useState(null)
  const [profilePicPreview, setProfilePicPreview] = useState('')
  const [selectedAvatar, setSelectedAvatar] = useState(null)
  const [avatarModalOpen, setAvatarModalOpen] = useState(false)
  const [form, setForm] = useState(DEFAULT_FORM)
  const [initialForm, setInitialForm] = useState(null)

  useEffect(() => {
    if (!open) return

    service.get(endpoints.users.countries, {}, { silent: true }).then(({ data }) => {
      setCountries(data?.data || [])
    })
    service.get(endpoints.users.languages, {}, { silent: true }).then(({ data }) => {
      setLanguages(data?.data || [])
    })
    service.get(endpoints.users.currencies, {}, { silent: true }).then(({ data }) => {
      setCurrencies(data?.data || [])
    })
  }, [open])

  useEffect(() => {
    if (!open) return

    const hydrate = (account) => {
      const hydrated = mapAccountToForm(account)
      const avatarId = resolveAvatarId(account)
      const imageUrl = resolveProfileImage(account) || profilePicUrl || ''
      setForm(hydrated)
      setInitialForm(hydrated)
      setProfilePicPreview(imageUrl)
      setProfilePicFile(null)
      setSelectedAvatar(
        avatarId !== '' && avatarId != null
          ? {
            id: Number(avatarId) || avatarId,
            image: typeof account.avatar === 'object' ? (account.avatar?.image || imageUrl) : imageUrl,
          }
          : null
      )
      setAvatarModalOpen(false)
    }

    if (initialData) {
      hydrate(initialData)
      return
    }

    service.get(endpoints.users.userAccount, {}, { silent: true }).then(({ data }) => {
      hydrate(data?.data || {})
    })
  }, [open, initialData, profilePicUrl])

  useEffect(() => {
    if (!open || !form.country_id) {
      setCities([])
      return
    }
    const url = endpoints.users.cities.replace('{country_id}', form.country_id)
    service.get(url, {}, { silent: true }).then(({ data }) => {
      setCities(data?.data || [])
    })
  }, [open, form.country_id])

  const handleFieldChange = (field) => (e) => {
    const rawValue = e.target.type === 'checkbox' ? e.target.checked : e.target.value
    const value = field === 'phone'
      ? String(rawValue).replace(/\D/g, '').slice(0, 10)
      : rawValue

    if (field === 'country_id') {
      const selectedCountry = countries.find((item) => String(item.id) === String(value))
      setForm((prev) => ({
        ...prev,
        country_id: value,
        city_id: '',
        phone_code: selectedCountry?.dial_code || prev.phone_code,
      }))
      return
    }

    setForm((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleProfilePicChange = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setProfilePicFile(file)
    setProfilePicPreview(URL.createObjectURL(file))
    setSelectedAvatar(null)
    setForm((prev) => ({ ...prev, avatar: '' }))
  }

  const handleApplyAvatar = (avatar) => {
    if (!avatar) return
    setSelectedAvatar(avatar)
    setProfilePicPreview(avatar.image || '')
    setProfilePicFile(null)
    setForm((prev) => ({ ...prev, avatar: avatar.id ?? '' }))
    setAvatarModalOpen(false)
  }

  const handleClose = () => {
    setProfilePicFile(null)
    setAvatarModalOpen(false)
    onClose?.()
  }

  const handleProfileUpdate = async (e) => {
    e.preventDefault()
    if (form.phone && !/^\d{10}$/.test(form.phone)) {
      showToast('Mobile number must be exactly 10 digits', 'error')
      return
    }

    const payload = new FormData()
    const appendIfChanged = (key, value) => {
      if (!initialForm) return
      const current = value ?? ''
      const original = initialForm[key] ?? ''
      if (String(current) !== String(original)) {
        payload.append(key, String(current))
      }
    }

    appendIfChanged('first_name', form.first_name)
    appendIfChanged('last_name', form.last_name)
    appendIfChanged('gender', form.gender)
    appendIfChanged('dob', form.dob)
    appendIfChanged('country_id', form.country_id)
    appendIfChanged('city_id', form.city_id)
    appendIfChanged('phone', form.phone)
    appendIfChanged('phone_code', form.phone_code)
    appendIfChanged('language_id', form.language_id)
    appendIfChanged('currency', form.currency)
    appendIfChanged('notifications_enabled', Boolean(form.notifications_enabled))
    appendIfChanged('avatar', form.avatar)
    if (profilePicFile) payload.append('profile_pic', profilePicFile)

    if ([...payload.keys()].length === 0) {
      showToast('No changes to update', 'warning')
      return
    }

    setSaving(true)
    try {
      const { data } = await service.put(endpoints.users.updateUserProfile, payload, { silent: true })
      const account = data?.data || {}
      // Prefer API image, then selected avatar / local preview so header updates immediately
      const fallbackImage = selectedAvatar?.image || profilePicPreview || ''
      const withImage = withProfileImage(account, fallbackImage)
      onSaved?.(withImage)
      showToast(data?.message || 'Profile updated successfully', 'success')
      handleClose()
    } catch {
      // handled by service interceptor
    } finally {
      setSaving(false)
    }
  }

  const previewImage = selectedAvatar?.image || profilePicPreview || SampleUser

  return (
    <>
    <Modal open={open} onClose={handleClose} maxWidth={760} className='profileEditModal'>
      <div className='profileEditModal__title'>Edit Profile</div>
      <form className='profileEditModal__form' onSubmit={handleProfileUpdate}>
        <div className='profileEditModal__avatarRow'>
          <img src={previewImage} alt='' className='profileEditModal__avatar' />
          <div className='profileEditModal__avatarActions'>
            <label className='profileEditModal__uploadBtn'>
              Upload Image
              <input type='file' accept='image/*' onChange={handleProfilePicChange} hidden />
            </label>
            <button
              type='button'
              className='profileEditModal__uploadBtn profileEditModal__uploadBtn--outline'
              onClick={() => setAvatarModalOpen(true)}
            >
              Choose Avatar
            </button>
          </div>
        </div>

        <div className='profileEditModal__grid'>
          <div className='profileEditModal__field'>
            <span className='profileEditModal__label'>First Name</span>
            <input className='profileEditModal__input' value={form.first_name} onChange={handleFieldChange('first_name')} />
          </div>
          <div className='profileEditModal__field'>
            <span className='profileEditModal__label'>Last Name</span>
            <input className='profileEditModal__input' value={form.last_name} onChange={handleFieldChange('last_name')} />
          </div>
          <div className='profileEditModal__field'>
            <span className='profileEditModal__label'>Gender</span>
            <SearchableSelect
              options={[{ value: 'M', label: 'Male' }, { value: 'F', label: 'Female' }, { value: 'O', label: 'Other' }]}
              value={form.gender}
              onChange={(gender) => setForm((prev) => ({ ...prev, gender }))}
            />
          </div>
          <div className='profileEditModal__field'>
            <span className='profileEditModal__label'>Date of Birth</span>
            <DatePicker
              value={form.dob}
              onChange={(dob) => setForm((prev) => ({ ...prev, dob }))}
              max={new Date().toISOString().split('T')[0]}
            />
          </div>
          <div className='profileEditModal__field'>
            <span className='profileEditModal__label'>Country</span>
            <SearchableSelect
              options={countries.map((item) => ({ value: item.id, label: item.name }))}
              value={form.country_id}
              onChange={(country_id) => {
                const selectedCountry = countries.find((item) => String(item.id) === String(country_id))
                setForm((prev) => ({
                  ...prev,
                  country_id,
                  city_id: '',
                  phone_code: selectedCountry?.dial_code || prev.phone_code,
                }))
              }}
              placeholder='Select Country'
            />
          </div>
          <div className='profileEditModal__field'>
            <span className='profileEditModal__label'>City</span>
            <SearchableSelect
              options={cities.map((item) => ({ value: item.id, label: item.name }))}
              value={form.city_id}
              onChange={(city_id) => setForm((prev) => ({ ...prev, city_id }))}
              disabled={!form.country_id}
              placeholder='Select City'
            />
          </div>
          <div className='profileEditModal__field'>
            <span className='profileEditModal__label'>Phone Code</span>
            <SearchableSelect
              options={countries.map((item) => ({ value: item.dial_code || '', label: `${item.code} ${item.dial_code}` }))}
              value={form.phone_code}
              onChange={(phone_code) => setForm((prev) => ({ ...prev, phone_code }))}
              placeholder='Select Phone Code'
            />
          </div>
          <div className='profileEditModal__field'>
            <span className='profileEditModal__label'>Mobile Number</span>
            <input className='profileEditModal__input' value={form.phone} onChange={handleFieldChange('phone')} maxLength={10} />
          </div>
          <div className='profileEditModal__field'>
            <span className='profileEditModal__label'>Language</span>
            <SearchableSelect
              options={languages.map((item) => ({ value: item.id, label: item.name }))}
              value={form.language_id}
              onChange={(language_id) => setForm((prev) => ({ ...prev, language_id }))}
              placeholder='Select Language'
            />
          </div>
          <div className='profileEditModal__field'>
            <span className='profileEditModal__label'>Currency</span>
            <SearchableSelect
              options={currencies.map((item) => ({ value: item.code || item.name, label: getCurrencyLabel(item) }))}
              value={form.currency}
              onChange={(currency) => setForm((prev) => ({ ...prev, currency }))}
              placeholder='Select Currency'
            />
          </div>
          <div className='profileEditModal__field'>
            <span className='profileEditModal__label'>Avatar Id</span>
            <input
              className='profileEditModal__input'
              type='number'
              value={form.avatar}
              readOnly
              placeholder='Select an avatar'
            />
          </div>
        </div>

        <label className='profileEditModal__checkRow'>
          <input
            type='checkbox'
            checked={Boolean(form.notifications_enabled)}
            onChange={handleFieldChange('notifications_enabled')}
          />
          <span>Notifications Enabled</span>
        </label>

        <div className='profileEditModal__actions'>
          <button type='button' className='bookingDownloadBtn' onClick={handleClose}>Cancel</button>
          <button type='submit' className='commonBtn' disabled={saving}>
            {saving ? 'Saving...' : 'Save Profile'}
          </button>
        </div>
      </form>
    </Modal>

    <AvatarModal
      open={avatarModalOpen}
      selected={selectedAvatar}
      onApply={handleApplyAvatar}
      onClose={() => setAvatarModalOpen(false)}
    />
    </>
  )
}

export default EditProfile
